import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, CheckCircle2, Briefcase, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { useSiteConfig } from "../../context/SiteConfigContext";
import SeoHead from "../../components/SeoHead";

const SUBJECTS = ["Partnership Inquiry", "Donation", "Consultancy", "Careers / Jobs", "Media", "General Inquiry"];

export default function Contact() {
    const { config } = useSiteConfig();
    const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", subject: "Partnership Inquiry", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/inquiries", form);
            setDone(true);
            setForm({ name: "", email: "", phone: "", organization: "", subject: "Partnership Inquiry", message: "" });
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!config) return null;

    return (
        <div data-testid="contact-page">
            <SeoHead pageKey="contact" />
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">Contact / Partner With Us</div>
                <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] max-w-4xl mb-6">Let's build resilience, together.</h1>
                <p className="text-lg text-muted-foreground max-w-3xl">
                    Donors, peers, government partners, consultants, and job seekers — we'd love to hear from you.
                </p>
                <Link
                    to="/careers"
                    className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors text-sm font-medium"
                    data-testid="contact-careers-link"
                >
                    <Briefcase size={14} /> View open positions <ArrowRight size={14} />
                </Link>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-12 gap-10">
                {/* Contact info */}
                <aside className="lg:col-span-4">
                    <div className="bg-card border border-border rounded-sm p-8 space-y-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Email</div>
                            <a href={`mailto:${config.contact_email}`} className="text-base font-medium inline-flex items-center gap-2" data-testid="contact-email">
                                <Mail size={16} className="text-primary" /> {config.contact_email}
                            </a>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Phone</div>
                            <a href={`tel:${config.contact_phone}`} className="text-base font-medium inline-flex items-center gap-2" data-testid="contact-phone">
                                <Phone size={16} className="text-primary" /> {config.contact_phone}
                            </a>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Office</div>
                            <div className="text-base inline-flex items-start gap-2">
                                <MapPin size={16} className="text-primary mt-1" /> {config.contact_address}
                            </div>
                        </div>
                        <div className="border-t border-border pt-6">
                            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">We work on</div>
                            <ul className="text-sm space-y-1.5 text-foreground/80">
                                <li>• Partnership Requests</li>
                                <li>• Consultancy Inquiries</li>
                                <li>• Careers & Jobs</li>
                                <li>• Donations & Funding</li>
                            </ul>
                        </div>
                    </div>
                </aside>

                {/* Form */}
                <div className="lg:col-span-8">
                    {done ? (
                        <div className="bg-card border border-border rounded-sm p-12 text-center" data-testid="inquiry-success">
                            <CheckCircle2 size={40} className="text-secondary mx-auto mb-4" />
                            <h2 className="font-display text-3xl mb-3">Thank you</h2>
                            <p className="text-muted-foreground mb-8">Your message has reached our team. We'll get back to you shortly.</p>
                            <button onClick={() => setDone(false)} className="px-6 py-2.5 rounded-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-sm">
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="bg-card border border-border rounded-sm p-8 lg:p-10 space-y-5" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="field-name" />
                                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="field-email" />
                                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="field-phone" />
                                <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="field-org" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Subject</label>
                                <select
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    className="w-full px-4 py-3 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    data-testid="field-subject"
                                >
                                    {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Message</label>
                                <textarea
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="w-full px-4 py-3 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                                    data-testid="field-message"
                                />
                            </div>
                            {error && <div className="text-sm text-destructive" data-testid="contact-error">{error}</div>}
                            <button
                                type="submit"
                                disabled={submitting}
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-60"
                                data-testid="contact-submit"
                            >
                                {submitting ? "Sending…" : <>Send message <Send size={14} /></>}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
    <div>
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}{required ? " *" : ""}</label>
        <input
            type={type}
            required={required}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-3 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid={testid}
        />
    </div>
);
